/**
 * Task history's rule-assessment pill. Resolution and its date are independent board columns.
 */
import { MetadataEntityFieldData, MetadataEnumTypeData, ServiceHistoryResult, ServiceHistoryRow } from '@jms/model';
import type { SeverityType } from '@workbuddy/ui/components/badge';

/** What the Result cell draws for one row. */
export interface ServiceHistoryResultPill {
  label: string;
  severity: SeverityType;
  /** The label, plus who resolved it. */
  tooltip: string;
}

/**
 * The pill for a row, or null when the row has no result yet. The colour comes from the `result` field's enum
 * options, so the API metadata stays the one place severities are decided; the text is the row's
 * `resultLabel`, which carries the detail the separate Detail column used to repeat.
 */
export function serviceHistoryResultPill(row: Pick<ServiceHistoryRow, 'result' | 'resultLabel' | 'completedOn' | 'completedBy' | 'earliest' | 'latest' | 'required' | 'completed'> | null | undefined, field: Pick<MetadataEntityFieldData, 'typeData'> | null | undefined): ServiceHistoryResultPill | null {

  if (!row) return null;
  const options = (Array.isArray(field?.typeData) ? field.typeData : []) as MetadataEnumTypeData[];
  const option = options.find(candidate => candidate.value === row.result);
  const resultLabel = row.resultLabel?.trim();
  // Older API responses used "No rule"; show the same business wording during staggered rollouts.
  const label = resultLabel === 'No rule' ? 'Not applicable' : resultLabel || option?.key || row.result || (row.completedOn ? 'Not applicable' : undefined);
  if (!label) return null;
  const by = row.completedBy?.name?.trim();
  const done = reportDate(row.completedOn);
  const earliest = reportDate(row.earliest);
  const latest = reportDate(row.latest);
  let explanation = label;
  switch (row.result) {
    case ServiceHistoryResult.Early:
      explanation = 'Resolved before the earliest allowed date.';
      if (done && earliest) explanation = `Resolved on ${done}, before the earliest allowed date of ${earliest}.`;
      break;
    case ServiceHistoryResult.Late:
      explanation = 'Resolved after the latest allowed date.';
      if (done && latest) explanation = `Resolved on ${done}, after the latest allowed date of ${latest}.`;
      break;
    case ServiceHistoryResult.Within:
      explanation = 'Resolved within the allowed window.';
      if (done && earliest && latest) explanation = `Resolved on ${done}, within the allowed window of ${earliest} to ${latest}.`;
      break;
    case ServiceHistoryResult.NotAssessed:
      explanation = 'This task has not been resolved yet, so its timing cannot be assessed.';
      if (label === 'Not applicable') explanation = 'No timing or frequency requirement is configured for this task.';
      else if (done) explanation = `Resolved on ${done}. No timing or frequency assessment applies.`;
      break;
    case ServiceHistoryResult.Met:
    case ServiceHistoryResult.Short:
      if (typeof row.completed === 'number' && typeof row.required === 'number') explanation = `${label}. ${row.completed} completions recorded against a target of ${row.required} for this period.`;
      break;
    case ServiceHistoryResult.Unresolved:
      explanation = 'This task has not been resolved.';
      break;
    default:
      if (label === 'Not applicable') explanation = 'No timing or frequency requirement is configured for this task.';
  }

  return {
    label,
    severity: (option?.severity as SeverityType) ?? 'neutral',
    tooltip: by ? `${explanation} Resolved by ${by}.` : explanation,
  };
}

/** Calendar dates stay in their recorded local day, with an unambiguous month name for the explanation. */
function reportDate(value: string | undefined): string | undefined {

  if (!value) return undefined;
  const date = new Date(`${value}T00:00:00.000Z`);
  if (Number.isNaN(date.getTime())) return value;
  return new Intl.DateTimeFormat('en-AU', { day: 'numeric', month: 'short', year: 'numeric', timeZone: 'UTC' }).format(date);
}
